import React, { useCallback, useEffect, useState, useMemo } from "react";
import { Calendar, ChevronDown, ChevronRight, Download, AlertCircle, Lock } from "react-feather";
import CustomPagination from "../../../BaseComponents/BasePagination";
import { salaryEnums } from "../../../Constant/HR/staffSalary";
import BaseButton from "../../../BaseComponents/BaseButton";
import BackButton from "../../../BaseComponents/BackButton";
import { usePageLoader } from "../../../_helper/loading/LoadingContext";
import { salaryOverviewTitle } from "../../../Constant/title";
import { listOfSalaryOverviewApi, viewSalaryOverviewApi } from "../../../Api/salaryApi";
import { viewStaffApi, viewProfileApi } from "../../../Api/HRStaffApi";
import { useNavigate, useParams } from "react-router-dom";
import {
  checkStatusCodeSuccess,
  getItem,
  getLocalItem,
  isUserProfile,
} from "../../../Constant/common";
import { toast } from "react-toastify";
import { notFound, Submit } from "../../../Constant";
import BaseModal from "../../../BaseComponents/BaseModal";
import { validationMessages } from "../../../Constant/validation";
import { useFormik } from "formik";
import * as yup from "yup";
import { halfSalarySlipApi } from "../../../Api/reportApi";
import "react-datepicker/dist/react-datepicker.css";
import DatePicker from "react-datepicker";
import "../Payroll/PayrollHistory/BatchDetail.css";
import "./salaryOverview.css";

const formatINR = (val) => {
  if (val == null || isNaN(val)) return "₹0";
  return `₹${Number(val).toLocaleString("en-IN")}`;
};

/**
 * Check if the salary record belongs to the current ongoing month or future month
 * that has not yet ended, so it can be hidden until the month is completed.
 */
const isOngoingCurrentMonth = (entry) => {
  if (!entry) return false;
  const dateStr = entry.date || entry.month;
  if (!dateStr) return false;

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth(); // 0 to 11

  // Parse entry date e.g. "September 2026"
  const entryDate = new Date(dateStr);
  if (!isNaN(entryDate.getTime())) {
    const entryYear = entryDate.getFullYear();
    const entryMonth = entryDate.getMonth();

    // If future year or future month in current year -> hide
    if (entryYear > currentYear || (entryYear === currentYear && entryMonth > currentMonth)) {
      return true;
    }

    // If current ongoing month -> hide until month ends
    if (entryYear === currentYear && entryMonth === currentMonth) {
      return true;
    }
  }

  // Also check duration string if available e.g. "01 September 2026 - 01 September 2026"
  if (entry.duration && typeof entry.duration === "string") {
    const parts = entry.duration.split("-").map((s) => s.trim());
    if (parts.length > 0) {
      const startDate = new Date(parts[0]);
      if (!isNaN(startDate.getTime())) {
        if (startDate.getFullYear() === currentYear && startDate.getMonth() === currentMonth) {
          return true;
        }
        if (
          startDate.getFullYear() > currentYear ||
          (startDate.getFullYear() === currentYear && startDate.getMonth() > currentMonth)
        ) {
          return true;
        }
      }
    }
  }

  return false;
};

const SalaryOverview = () => {
  const { staffId } = useParams();
  const [openDetailsId, setOpenDetailsId] = useState(null);
  const { setLoading } = usePageLoader();
  const [salaryOverviewList, setSalaryOverviewList] = useState(null);
  const [salaryOverviewDetails, setSalaryOverviewDetails] = useState({});
  const [staffData, setStaffData] = useState(null);
  const [modal, setModal] = useState(false);
  const [btnLoader, setBtnLoader] = useState(false);

  // ── Pagination state ──────────────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [columnName] = useState("id");
  const [sortOrder] = useState("DESC");
  const [searchQuery, setSearchQuery] = useState("");

  const isProfile = JSON.parse(getItem(isUserProfile) || false);
  const profileId = getLocalItem("id");
  const navigate = useNavigate();
  const finalId = isProfile ? profileId : staffId;

  useEffect(() => {
    document.title = salaryOverviewTitle;
  }, []);

  const toggleDetails = (dateKey, entry, isPayrollNotRun = false) => {
    if (isPayrollNotRun) {
      toast.info(
        `Payroll has not been run for ${dateKey || "this month"}. Please run payroll first.`,
      );
      return;
    }

    if (openDetailsId !== dateKey) {
      if (!salaryOverviewDetails[dateKey]) {
        setLoading(true);
        viewSalaryOverviewApi(finalId, { month: dateKey })
          .then((resp) => {
            if (checkStatusCodeSuccess(resp?.statusCode)) {
              const data = resp?.data;
              let matched = null;
              if (Array.isArray(data)) {
                matched =
                  data.find((item) => item.date === dateKey || item.month === dateKey) || data[0];
              } else if (data && Array.isArray(data.paginatedOverviewList)) {
                matched =
                  data.paginatedOverviewList.find(
                    (item) => item.date === dateKey || item.month === dateKey,
                  ) || data.paginatedOverviewList[0];
              } else if (data && typeof data === "object") {
                matched = data;
              }
              setSalaryOverviewDetails((prev) => ({
                ...prev,
                [dateKey]: matched || entry,
              }));
            } else {
              setSalaryOverviewDetails((prev) => ({
                ...prev,
                [dateKey]: entry,
              }));
            }
          })
          .catch(() => {
            setSalaryOverviewDetails((prev) => ({
              ...prev,
              [dateKey]: entry,
            }));
          })
          .finally(() => setLoading(false));
      }
    }
    setOpenDetailsId(openDetailsId === dateKey ? null : dateKey);
  };

  const fetchData = useCallback(() => {
    setLoading(true);
    const staffApiCall = isProfile ? viewProfileApi(finalId || "") : viewStaffApi(finalId);
    Promise.all([
      listOfSalaryOverviewApi(finalId, {
        order: [columnName, sortOrder],
        pageSize,
        pageNumber: currentPage,
        search: searchQuery,
      }).catch(() => null),
      staffApiCall.catch(() => null),
    ])
      .then(([resp, staffResp]) => {
        if (resp && checkStatusCodeSuccess(resp?.statusCode)) {
          const data = resp?.data || {};
          const records = Array.isArray(data)
            ? data
            : Array.isArray(data.paginatedOverviewList)
              ? data.paginatedOverviewList
              : [];
          setSalaryOverviewList(records);
          setTotalPages(data.totalPages ?? 1);
          setCurrentPage(data.currentPage ?? 1);
        } else if (resp) {
          toast.error(resp?.message);
        }
        if (staffResp) {
          const staffObj = staffResp?.data || staffResp;
          setStaffData(staffObj);
        }
      })
      .catch(() => setSalaryOverviewList([]))
      .finally(() => setLoading(false));
  }, [finalId, isProfile, setLoading, columnName, sortOrder, pageSize, currentPage, searchQuery]);

  useEffect(() => {
    const t = setTimeout(fetchData, 0);
    return () => clearTimeout(t);
  }, [fetchData]);

  // Filter out the ongoing current/future month that has not ended yet
  const visibleOverviewList = useMemo(() => {
    if (!Array.isArray(salaryOverviewList)) return [];
    return salaryOverviewList.filter((entry) => !isOngoingCurrentMonth(entry));
  }, [salaryOverviewList]);

  /* ── Pagination handlers ── */
  const handlePageChange = (p) => setCurrentPage(p);
  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((p) => p + 1);
  };
  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage((p) => p - 1);
  };
  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };
  const handleSearchChange = useCallback((v) => {
    if (v !== undefined) setSearchQuery(v);
    setCurrentPage(1);
  }, []);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: { selectedDate: null },
    validationSchema: yup.object({
      selectedDate: yup.string().required(validationMessages.required(salaryEnums.MONTH_YEAR)),
    }),
    onSubmit: async (values) => {
      setBtnLoader(true);
      const d = new Date(values.selectedDate);
      const monthName = new Intl.DateTimeFormat("en-US", { month: "long" }).format(d);
      await halfSalarySlipApi({
        staff_id: Number(finalId),
        month: String(monthName),
        year: String(d.getFullYear()),
      })
        .then((resp) => {
          if (checkStatusCodeSuccess(resp?.status)) {
            const blob = new Blob([resp?.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const staffName = JSON.parse(getItem("staffData"))?.name || "Salary";
            a.download = `${staffName}${salaryEnums.FILENAME}`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            toast.success(resp?.success);
          } else toast.error(resp?.message);
        })
        .catch((err) => toast.error(err.response?.data?.message || err.message))
        .finally(() => {
          setBtnLoader(false);
          formik.resetForm();
          setModal(false);
        });
    },
  });

  const countDisplay = visibleOverviewList.length;

  return (
    <div className="animate-fade-in salary-overview-container">
      {/* Header */}
      <div className="salary-overview-header">
        <div>
          <div className="salary-overview-title-row">
            <h2 className="salary-overview-title">{salaryEnums.SALARY_OVERVIEW}</h2>
          </div>
          <p className="salary-overview-subtitle">
            {countDisplay > 0
              ? `${countDisplay} month${countDisplay !== 1 ? "s" : ""} recorded · Overview & Employee Breakdown`
              : "Monthly salary & payroll overview"}
          </p>
        </div>
        <div className="salary-overview-header-actions">
          <BaseButton
            color="primary"
            onClick={() => setModal(true)}
            className="salary-overview-generate-btn"
          >
            <Download size={14} strokeWidth={2} /> {salaryEnums.GENERATE_SALARY_SLIP}
          </BaseButton>
          <BackButton
            onClick={() =>
              isProfile ? navigate("/profile") : navigate(`/staffProfile/${staffId}`)
            }
          />
        </div>
      </div>

      {/* Salary slip modal */}
      <BaseModal
        isOpen={modal}
        title={salaryEnums.SALARY_SLIP}
        toggler={() => setModal(false)}
        submit={formik.handleSubmit}
        submitText={Submit}
        disabled={btnLoader}
        loader={btnLoader}
      >
        <form onSubmit={formik.handleSubmit}>
          <label className="form-label">{salaryEnums.SELECT_YEAR_MONTH}</label>
          <DatePicker
            className="form-control"
            selected={formik.values.selectedDate}
            onChange={(d) => formik.setFieldValue("selectedDate", d)}
            placeholderText={salaryEnums.SELECT_YEAR_MONTH}
            showMonthYearPicker
            dateFormat="MM/yyyy"
            portalId="root"
            popperProps={{ strategy: "fixed" }}
            popperPlacement="bottom-start"
          />
          {formik.touched.selectedDate && formik.errors.selectedDate && (
            <div className="invalid-feedback d-block">{formik.errors.selectedDate}</div>
          )}
        </form>
      </BaseModal>

      {/* Salary cards wrapped in CustomPagination */}
      <CustomPagination
        pageSize={pageSize}
        totalRecords={visibleOverviewList.length}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        onNextPage={handleNextPage}
        onPreviousPage={handlePreviousPage}
        onPageSizeChange={handlePageSizeChange}
        onSearchChange={handleSearchChange}
        searchQuery={searchQuery}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 12 }}>
          {visibleOverviewList.map((entry, index) => {
            const dateKey = entry?.date;
            const isOpen = openDetailsId === dateKey;
            const detail = salaryOverviewDetails[dateKey] || {};
            const merged = { ...entry, ...detail };

            // Detect if payroll-based from organization or merged salary record
            const orgPayroll =
              staffData?.organization?.is_payroll ??
              staffData?.organization?.isPayroll ??
              staffData?.is_payroll ??
              staffData?.isPayroll;

            const isPayroll =
              typeof orgPayroll === "boolean"
                ? orgPayroll
                : Boolean(
                    merged?.isPayroll === true ||
                    merged?.salary_type === "payroll" ||
                    merged?.is_payroll_based === true ||
                    Number(merged?.totalDeductions) > 0 ||
                    Number(merged?.pfEmployee) > 0 ||
                    Number(merged?.pfEmployer) > 0 ||
                    Number(merged?.esicAmount) > 0 ||
                    Number(merged?.ptAmount) > 0 ||
                    Number(merged?.tdsAmount) > 0 ||
                    (Array.isArray(merged?.deductions) && merged.deductions.length > 0),
                  );

            const grossSalary = Number(
              merged?.grossSalary ?? merged?.totalMonthlyCtc ?? merged?.monthlySalary ?? 0,
            );

            const pfEmployee = Number(merged?.pfEmployee ?? merged?.pf ?? 0);
            const pfEmployer = Number(merged?.pfEmployer ?? 0);
            const esicAmount = Number(merged?.esicAmount ?? merged?.esic ?? 0);
            const ptAmount = Number(merged?.ptAmount ?? merged?.pt ?? 0);
            const tdsAmount = Number(merged?.tdsAmount ?? merged?.tds ?? 0);
            const variableAmount = Number(merged?.variableAmount ?? merged?.variable_amount ?? 0);

            const calculatedDeductions = pfEmployee + esicAmount + ptAmount + tdsAmount;
            const totalDeductions = Number(merged?.totalDeductions ?? calculatedDeductions);

            const finalPayableSalary = Number(
              merged?.finalPayableSalary ?? (grossSalary > 0 ? grossSalary - totalDeductions : 0),
            );

            // Determine if payroll has been run for this month
            const isPayrollRun = Boolean(
              merged?.isPayrollRun === true ||
              merged?.is_payroll_run === true ||
              merged?.isRun === true ||
              merged?.is_run === true ||
              merged?.payroll_status === "completed" ||
              merged?.payroll_status === "paid" ||
              merged?.status === "paid" ||
              merged?.status === "completed" ||
              (merged?.isPayrollRun !== false &&
                merged?.is_payroll_run !== false &&
                (grossSalary > 0 || finalPayableSalary > 0)),
            );

            const isPayrollNotRun = isPayroll && !isPayrollRun;

            // Safely extract and parse earnings and deductions
            let rawEarnings = merged?.earnings;
            let rawDeductions = merged?.deductions;

            if (typeof rawEarnings === "string") {
              try {
                rawEarnings = JSON.parse(rawEarnings);
              } catch (e) {
                // Ignore JSON parse error and keep raw value
              }
            }
            if (typeof rawDeductions === "string") {
              try {
                rawDeductions = JSON.parse(rawDeductions);
              } catch (e) {
                // Ignore JSON parse error and keep raw value
              }
            }

            // If earnings or deductions are not directly provided as arrays, check component_breakdown / componentBreakdown
            if (
              (!Array.isArray(rawEarnings) || rawEarnings.length === 0) &&
              (!Array.isArray(rawDeductions) || rawDeductions.length === 0)
            ) {
              let breakdown = merged?.component_breakdown || merged?.componentBreakdown;
              if (typeof breakdown === "string") {
                try {
                  breakdown = JSON.parse(breakdown);
                } catch (e) {
                  // Ignore JSON parse error and keep raw value
                }
              }
              if (Array.isArray(breakdown)) {
                rawEarnings = breakdown.filter(
                  (c) =>
                    c.component_type === "Earning" || c.type === "Earning" || c.type === "earning",
                );
                rawDeductions = breakdown.filter(
                  (c) =>
                    c.component_type === "Deduction" ||
                    c.type === "Deduction" ||
                    c.type === "deduction",
                );
              }
            }

            // Construct Earnings list (fixed components only)
            let earningsList = [];
            if (Array.isArray(rawEarnings) && rawEarnings.length > 0) {
              earningsList = rawEarnings
                .filter((e) => {
                  const compName = (
                    e.component_name ||
                    e.componentName ||
                    e.name ||
                    ""
                  ).toLowerCase();
                  if (/variable|overtime/i.test(compName)) return false;
                  if (isPayroll && compName.includes("special allowance")) return false;
                  return true;
                })
                .map((e) => ({
                  name:
                    e.component_name ||
                    e.componentName ||
                    e.name ||
                    e.label ||
                    e.title ||
                    "Earning Component",
                  amount: Number(e.amount ?? 0),
                  isTaxable: e.is_taxable ?? e.isTaxable,
                }));
            } else {
              const basicAmount = Number(
                merged?.actualSystemBasis ??
                  merged?.monthlySalary ??
                  Math.max(0, grossSalary - variableAmount),
              );
              if (basicAmount > 0 || grossSalary > 0) {
                earningsList.push({
                  name: "Basic Salary",
                  amount: basicAmount,
                });
                if (!isPayroll && Number(merged?.monthly_special_allowance) > 0) {
                  earningsList.push({
                    name: "Special Allowance",
                    amount: Number(merged.monthly_special_allowance),
                  });
                }
              }
            }

            // Construct Deductions list (employee deductions only)
            let deductionsList = [];
            if (Array.isArray(rawDeductions) && rawDeductions.length > 0) {
              deductionsList = rawDeductions
                .filter((d) => {
                  const compName = (
                    d.component_name ||
                    d.componentName ||
                    d.name ||
                    ""
                  ).toLowerCase();
                  return !compName.includes("employer") && !d.isEmployer && !d.is_employer;
                })
                .map((d) => ({
                  name:
                    d.component_name ||
                    d.componentName ||
                    d.name ||
                    d.label ||
                    d.title ||
                    "Deduction Component",
                  amount: Number(d.amount ?? 0),
                }));
            } else if (isPayroll) {
              if (pfEmployee > 0)
                deductionsList.push({ name: "PF (Employee)", amount: pfEmployee });
              if (esicAmount > 0) deductionsList.push({ name: "ESIC", amount: esicAmount });
              if (ptAmount > 0)
                deductionsList.push({ name: "Professional Tax (PT)", amount: ptAmount });
              if (tdsAmount > 0) deductionsList.push({ name: "TDS", amount: tdsAmount });
            }

            return (
              <div
                key={dateKey || index}
                className={`employee-row glass animate-slide-up salary-overview-row-card ${
                  isOpen ? "expanded open" : ""
                } ${isPayrollNotRun ? "not-run" : ""}`}
              >
                {/* ── Row Header ── */}
                <div
                  onClick={() => toggleDetails(dateKey, entry, isPayrollNotRun)}
                  role="button"
                  tabIndex={0}
                  aria-expanded={isOpen}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleDetails(dateKey, entry, isPayrollNotRun);
                    }
                  }}
                  className={`employee-row-header salary-overview-row-header ${
                    isOpen ? "open" : ""
                  } ${isPayrollNotRun ? "not-run" : ""}`}
                >
                  {/* Expand chevron or Lock */}
                  <div className="employee-row-chevron text-muted flex-shrink-0 d-flex align-items-center">
                    {isPayrollNotRun ? (
                      <Lock size={15} className="salary-overview-lock-icon" />
                    ) : isOpen ? (
                      <ChevronDown size={18} strokeWidth={2} />
                    ) : (
                      <ChevronRight size={18} strokeWidth={2} />
                    )}
                  </div>

                  {/* Icon */}
                  <div
                    className={`salary-overview-icon-box ${
                      !isPayroll ? "flat" : isPayrollNotRun ? "payroll-not-run" : "payroll-run"
                    }`}
                  >
                    <Calendar
                      size={20}
                      strokeWidth={2}
                      className={`salary-overview-cal-icon ${
                        !isPayroll ? "flat" : isPayrollNotRun ? "payroll-not-run" : "payroll-run"
                      }`}
                    />
                  </div>

                  {/* Month & Duration Column */}
                  <div className="employee-row-name-col salary-overview-name-col">
                    <div className="salary-overview-name-row">
                      <p className="employee-row-name salary-overview-month-name">
                        {dateKey || "Salary Month"}
                      </p>

                      {/* Badge: Payroll Based vs Flat Base */}
                      {isPayroll ? (
                        <span className="salary-overview-badge payroll">
                          <span className="salary-overview-badge-dot payroll" />
                          Payroll Based
                        </span>
                      ) : (
                        <span className="salary-overview-badge flat">
                          <span className="salary-overview-badge-dot flat" />
                          Flat Base
                        </span>
                      )}

                      {/* Payroll Not Run Note */}
                      {isPayrollNotRun && (
                        <span className="salary-overview-not-run-badge">
                          <AlertCircle
                            size={13}
                            strokeWidth={2.5}
                            className="salary-overview-not-run-icon"
                          />
                          Payroll not run · Please run payroll first
                        </span>
                      )}
                    </div>

                    <p className="employee-row-meta salary-overview-duration">
                      {entry?.duration || "Full Month Duration"}
                    </p>
                  </div>
                </div>

                {/* ── Expanded Employee Breakdown (Only when payroll has run or flat base) ── */}
                {isOpen && !isPayrollNotRun && (
                  <div className="employee-breakdown animate-slide-down">
                    {isPayroll ? (
                      /* ── PAYROLL BASED BREAKDOWN ── */
                      <>
                        {/* Earnings Section */}
                        <div className="earnings-section">
                          <div className="salary-overview-section-header-flex">
                            <p className="earnings-section-title salary-overview-section-title-clean">
                              Earnings
                            </p>
                            <span className="salary-overview-badge-tag indigo">Payroll Run</span>
                          </div>

                          {earningsList.length === 0 ? (
                            <p className="empty-breakdown-text">No earnings data</p>
                          ) : (
                            <table className="earnings-table">
                              <tbody>
                                {earningsList.map((e, i) => (
                                  <tr key={i}>
                                    <td>{e.name}</td>
                                    <td>{formatINR(e.amount)}</td>
                                  </tr>
                                ))}
                                <tr className="breakdown-total-row">
                                  <td>Gross Salary</td>
                                  <td>{formatINR(grossSalary)}</td>
                                </tr>
                              </tbody>
                            </table>
                          )}
                        </div>

                        {/* Deductions Section */}
                        <div className="deductions-section">
                          <div className="salary-overview-section-header-flex">
                            <p className="deductions-section-title salary-overview-section-title-clean">
                              Deductions
                            </p>
                            <span className="salary-overview-badge-tag rose">
                              Statutory & Taxes
                            </span>
                          </div>

                          {deductionsList.length === 0 ? (
                            <p className="empty-breakdown-text">No deductions recorded</p>
                          ) : (
                            <table className="deductions-table">
                              <tbody>
                                {deductionsList.map((d, i) => (
                                  <tr key={i}>
                                    <td>{d.name}</td>
                                    <td>{formatINR(d.amount)}</td>
                                  </tr>
                                ))}
                                <tr className="breakdown-total-row">
                                  <td>Total Deductions</td>
                                  <td>{formatINR(totalDeductions)}</td>
                                </tr>
                              </tbody>
                            </table>
                          )}

                          {/* PF Employer Note */}
                          {pfEmployer > 0 && (
                            <div className="employer-pf-note">
                              <span className="employer-pf-note-label">Note:</span>
                              <span>
                                Employer PF Contribution (informational):{" "}
                                <strong>{formatINR(pfEmployer)}</strong>
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Unified Variable Pay & Net Salary Full-Width Card */}
                        <div className="variable-net-banner">
                          {/* Variable Pay on the Left */}
                          {variableAmount > 0 ? (
                            <div className="variable-pay-left">
                              <div className="variable-pay-badge-icon">+</div>
                              <div>
                                <span className="variable-pay-label">Variable Pay / Overtime</span>
                                <span className="variable-pay-amount">
                                  {formatINR(variableAmount)}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <div className="salary-mini-breakdown">
                              <span className="salary-mini-text">
                                Gross:{" "}
                                <strong className="salary-mini-strong-primary">
                                  {formatINR(grossSalary)}
                                </strong>{" "}
                                · Deductions:{" "}
                                <strong className="salary-mini-strong-rose">
                                  {formatINR(totalDeductions)}
                                </strong>
                              </span>
                            </div>
                          )}

                          {/* Net Salary on the Right */}
                          <div className="net-salary-right">
                            <div className="net-salary-right-content">
                              <span className="net-salary-right-label">Net Salary</span>
                              <span className="net-salary-right-amount">
                                {formatINR(finalPayableSalary)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      /* ── FLAT BASE BREAKDOWN ── */
                      <>
                        {/* Earnings Section (Flat) */}
                        <div className="earnings-section">
                          <div className="salary-overview-section-header-flex">
                            <p className="earnings-section-title salary-overview-section-title-clean">
                              Salary Breakdown (Flat Base)
                            </p>
                            <span className="salary-overview-badge-tag gray">Fixed Rate</span>
                          </div>

                          <table className="earnings-table">
                            <tbody>
                              <tr>
                                <td>{salaryEnums.SYSTEM_BASIC}</td>
                                <td>
                                  {formatINR(
                                    merged?.actualSystemBasis ??
                                      merged?.monthlySalary ??
                                      grossSalary,
                                  )}
                                </td>
                              </tr>
                              {variableAmount > 0 && (
                                <tr>
                                  <td>{salaryEnums.VARIABLE_AMOUNT}</td>
                                  <td>{formatINR(variableAmount)}</td>
                                </tr>
                              )}
                              <tr className="breakdown-total-row">
                                <td>Gross Earning</td>
                                <td>{formatINR(grossSalary)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>

                        {/* Summary & Net Section (Flat) */}
                        <div className="deductions-section">
                          <div className="salary-overview-section-header-flex">
                            <p className="deductions-section-title salary-overview-title-indigo">
                              Summary & Payable
                            </p>
                            <span className="salary-overview-badge-tag gray">Flat Calculation</span>
                          </div>

                          <table className="deductions-table">
                            <tbody>
                              <tr>
                                <td>Statutory Deductions</td>
                                <td className="salary-overview-text-muted">None (₹0)</td>
                              </tr>
                              {merged?.totalPayableDays !== undefined &&
                                merged?.totalPayableDays !== null && (
                                  <tr>
                                    <td>Payable Days</td>
                                    <td className="salary-overview-text-primary">
                                      {merged?.totalPayableDays} Days
                                    </td>
                                  </tr>
                                )}
                              <tr className="breakdown-total-row">
                                <td>Total Deductions</td>
                                <td>₹0</td>
                              </tr>
                            </tbody>
                          </table>

                          {/* Net highlight */}
                          <div className="net-salary-highlight">
                            <span className="net-salary-label">Net Payable</span>
                            <span className="net-salary-amount">
                              {formatINR(finalPayableSalary)}
                            </span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {(!visibleOverviewList || visibleOverviewList.length < 1) && (
            <div className="salary-overview-empty">
              <AlertCircle size={40} className="salary-overview-empty-icon" />
              <p className="salary-overview-empty-text">{notFound.dataNotFound}</p>
            </div>
          )}
        </div>
      </CustomPagination>
    </div>
  );
};

export default SalaryOverview;
